<template>
  <div class="anamnesis-container">
    <Breadcrumbs />

    <div v-if="isLoading" class="text-center text-gray-600 mt-4">
      Chargement en cours...
    </div>

    <div v-else class="mb-8">
      <!-- Toolbar modernisée avec icônes -->
      <div class="anamnesis-detail-toolbar">
        <div class="anamnesis-detail-toolbar-content">
          <!-- Section titre avec icône et gestion des permissions -->
          <div class="anamnesis-detail-title-section">
            <!-- Icône Dataset avec couleur thématique -->
            <div class="flex items-center mr-3">
              <HardDriveIcon class="h-6 w-6 text-purple-600" />
            </div>

            <div class="relative flex-1">
              <!-- Édition du titre -->
              <input v-if="isEditingTitle && canEdit" ref="titleInput" v-model="editedTitle" type="text"
                placeholder="Nom du dataset" class="anamnesis-detail-title-edit" @blur="handleTitleUpdate"
                @keydown.enter="handleTitleUpdate" @keydown.escape="cancelTitleEdit" />

              <!-- Affichage normal -->
              <h1 v-else class="anamnesis-detail-title" :class="{ readonly: !canEdit }"
                @click="canEdit && startTitleEdit()">
                {{ digitalEntity?.appellation || 'Dataset sans nom' }}
              </h1>
            </div>

            <!-- Lien externe si URL disponible -->
            <div v-if="digitalEntity?.url && !isEditing" class="ml-4">
              <a :href="digitalEntity.url" target="_blank" rel="noopener noreferrer"
                class="anamnesis-detail-external-link">
                <LinkIcon class="h-4 w-4" />
                <span>Voir le fichier</span>
              </a>
            </div>

            <!-- Informations de format en mode édition -->
            <div v-if="isEditing && (digitalEntity?.format || digitalEntity?.version)"
              class="ml-4 text-sm text-gray-500">
              <span v-if="digitalEntity?.format">{{ digitalEntity.format }}</span>
              <span v-if="digitalEntity?.version">v{{ digitalEntity.version }}</span>
            </div>
          </div>

          <!-- Actions avec gestion des permissions -->
          <div class="anamnesis-detail-actions">
            <!-- Toggle public/privé - visible pour tous, modifiable seulement par le propriétaire -->
            <button class="anamnesis-detail-status" :class="[
              digitalEntity?.is_public ? 'public' : 'private',
              { disabled: !canEdit }
            ]" :disabled="!canEdit" @click="canEdit && handlePublicToggle()">
              <EyeIcon v-if="digitalEntity?.is_public" class="h-4 w-4" />
              <EyeOffIcon v-else class="h-4 w-4" />
              {{ digitalEntity?.is_public ? 'Public' : 'Privé' }}
            </button>

            <!-- Boutons d'action - seulement visibles pour les propriétaires/admin -->
            <template v-if="canEdit">
              <!-- Boutons d'édition -->
              <template v-if="isEditing">
                <button class="anamnesis-detail-btn-success" @click="handleSave">
                  <CheckIcon class="h-5 w-5" />
                  Valider
                </button>
                <button class="anamnesis-detail-btn-secondary" @click="handleCancel">
                  <XIcon class="h-5 w-5" />
                  Annuler
                </button>
              </template>
              <button v-else class="anamnesis-detail-btn-primary" @click="handleEdit">
                <PencilIcon class="h-5 w-5" />
              </button>

              <!-- Supprimer - seulement pour les propriétaires/admin -->
              <button v-if="canDelete" class="anamnesis-detail-btn-delete" @click="handleDelete">
                <TrashIcon class="h-5 w-5" />
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- Grille principale avec fond blanc et cadres -->
      <div class="anamnesis-detail-grid">
        <!-- Colonne de gauche : Détails -->
        <div class="anamnesis-detail-section bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
            Informations du dataset
          </h3>

          <!-- Type -->
          <div class="anamnesis-detail-field">
            <FolderIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <div v-if="isEditing">
                <input v-model="editedDigitalEntity.type" class="anamnesis-detail-field-input"
                  placeholder="Type de dataset" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ digitalEntity?.type || 'Type non spécifié' }}
              </p>
            </div>
          </div>

          <!-- Description -->
          <div class="anamnesis-detail-field">
            <AlignLeftIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <div v-if="isEditing">
                <textarea v-model="editedDigitalEntity.description" rows="3"
                  class="anamnesis-detail-field-input resize-none" placeholder="Description du dataset"></textarea>
              </div>
              <p v-else class="anamnesis-detail-field-text whitespace-pre-line">
                {{ digitalEntity?.description || 'Description non spécifiée' }}
              </p>
            </div>
          </div>

          <!-- URL -->
          <div class="anamnesis-detail-field">
            <Link2Icon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">URL</label>
              <div v-if="isEditing">
                <input v-model="editedDigitalEntity.url" type="url" class="anamnesis-detail-field-input"
                  placeholder="https://..." />
              </div>
              <div v-else>
                <a v-if="digitalEntity?.url" :href="digitalEntity.url" target="_blank" rel="noopener noreferrer"
                  class="anamnesis-detail-external-link">
                  <LinkIcon class="h-4 w-4" />
                  <span class="break-all">{{ digitalEntity.url }}</span>
                </a>
                <p v-else class="anamnesis-detail-field-empty">URL non spécifiée</p>
              </div>
            </div>
          </div>

          <!-- Format et Version -->
          <div class="anamnesis-detail-field">
            <FileTypeIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Format et Version</label>
              <div v-if="isEditing">
                <div class="flex gap-2">
                  <input v-model="editedDigitalEntity.format" class="flex-1 anamnesis-detail-field-input"
                    placeholder="Format (ex: JPG, PDF...)" />
                  <input v-model="editedDigitalEntity.version" class="w-24 anamnesis-detail-field-input"
                    placeholder="Version" />
                </div>
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ digitalEntity?.format || 'Format non spécifié' }}
                <span v-if="digitalEntity?.version" class="text-gray-500">
                  (v{{ digitalEntity.version }})
                </span>
              </p>
            </div>
          </div>

          <!-- Propriétaire -->
          <div class="anamnesis-detail-field">
            <UserIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Propriétaire</label>
              <div v-if="isEditing">
                <input v-model="editedDigitalEntity.owner" class="anamnesis-detail-field-input"
                  placeholder="Propriétaire du dataset" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ digitalEntity?.owner || 'Propriétaire non spécifié' }}
              </p>
            </div>
          </div>

          <!-- Copyright -->
          <div class="anamnesis-detail-field">
            <CopyrightIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Copyright</label>
              <div v-if="isEditing">
                <input v-model="editedDigitalEntity.copyright" class="anamnesis-detail-field-input"
                  placeholder="Informations de copyright" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ digitalEntity?.copyright || 'Copyright non spécifié' }}
              </p>
            </div>
          </div>

          <!-- Nombre de photos -->
          <div v-if="isPhotographyType(digitalEntity?.type)" class="anamnesis-detail-field">
            <LinkIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Lien</label>
              <div v-if="isEditing">
                <input v-model="editedDigitalEntity.de_link"
                  class="anamnesis-detail-field-input" placeholder="https://..." />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ digitalEntity?.de_link || 'Lien non spécifié' }}
              </p>
            </div>
          </div>

          <div class="anamnesis-detail-field">
            <FileTextIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Spécifications fichier</label>
              <div v-if="isEditing">
                <input v-model="editedDigitalEntity.file_spec"
                  class="anamnesis-detail-field-input" placeholder="Format, résolution, etc." />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ digitalEntity?.file_spec || 'Spécifications non spécifiées' }}
              </p>
            </div>
          </div>
        </div>

        <!-- Colonne de droite : Aperçu et métadonnées -->
        <div class="anamnesis-detail-metadata bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Aperçu et métadonnées</h3>

          <!-- Aperçu -->
          <div class="mb-6">
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 h-48 flex items-center justify-center">
              <!-- Si URL est une image, on l'affiche -->
              <div v-if="isImageUrl(digitalEntity?.url)" class="w-full h-full flex items-center justify-center">
                <img :src="digitalEntity?.url" alt="Aperçu" class="max-h-full max-w-full object-contain rounded" />
              </div>
              <!-- Sinon on affiche une icône selon le type -->
              <div v-else class="text-center">
                <component :is="getIconForType(digitalEntity?.type, digitalEntity?.format)"
                  class="h-16 w-16 mx-auto text-gray-400" />
                <p class="mt-2 text-gray-500 text-sm">Aperçu non disponible</p>
                <a v-if="digitalEntity?.url" :href="digitalEntity.url" target="_blank" rel="noopener noreferrer"
                  class="mt-2 inline-flex items-center text-blue-600 hover:text-blue-800 text-sm">
                  Voir le fichier
                  <ExternalLinkIcon class="h-4 w-4 ml-1" />
                </a>
              </div>
            </div>
            <div v-if="isEditing" class="mt-4 p-3 bg-blue-50 text-blue-800 rounded-lg text-sm">
              Pour changer l'aperçu, mettez à jour l'URL avec un lien vers une ressource en ligne.
            </div>
          </div>

          <!-- Métadonnées -->
          <div class="anamnesis-detail-meta-item">
            <ClockIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Créé le</p>
              <p class="anamnesis-detail-meta-text">
                {{ formatDate(digitalEntity?.created_at) }}
              </p>
            </div>
          </div>

          <div v-if="digitalEntity?.updated_at && digitalEntity.updated_at !== digitalEntity.created_at"
            class="anamnesis-detail-meta-item">
            <ClockIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Modifié le</p>
              <p class="anamnesis-detail-meta-text">
                {{ formatDate(digitalEntity?.updated_at) }}
              </p>
            </div>
          </div>

          <div v-if="detailedMemos.length" class="anamnesis-detail-meta-item">
            <BookOpenIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Mémos associés</p>
              <p class="anamnesis-detail-meta-text">
                {{ detailedMemos.length }} mémo(s)
              </p>
            </div>
          </div>

          <div class="anamnesis-detail-meta-item">
            <UserIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Propriétaire</p>
              <p class="anamnesis-detail-meta-text">
                {{ ownerName || 'Chargement...' }}
              </p>
            </div>
          </div>

          <!-- Type de dataset -->
          <div class="anamnesis-detail-meta-item">
            <HardDriveIcon class="h-5 w-5 text-purple-600 mr-2" />
            <div>
              <p class="text-sm font-medium text-gray-700">Type</p>
              <p class="anamnesis-detail-meta-text">
                Dataset numérique
                <span v-if="digitalEntity?.type" class="text-gray-500">
                  ({{ digitalEntity.type }})
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Section Mémos avec fond blanc -->
      <div v-if="canViewSubsets" class="anamnesis-detail-memos-section bg-white rounded-lg shadow-sm p-6">
        <h2 class="anamnesis-detail-memos-title">Mémos associés</h2>
        <div v-if="detailedMemos.length">
          <MemosSubset :memos="detailedMemos" :show-delete-button="false" @view="handleMemoView" />
        </div>
        <div v-else class="anamnesis-detail-memos-empty">
          Aucun mémo associé à ce dataset
        </div>
      </div>
      
      <!-- Message d'accès restreint -->
      <div v-else class="anamnesis-detail-memos-section bg-white rounded-lg shadow-sm p-6">
        <div class="text-center py-8">
          <EyeOffIcon class="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">Accès restreint</h3>
          <p class="text-gray-600">
            {{ !authStore.isAuthenticated ? 'Connectez-vous pour voir les mémos associés' : 'Cet asset privé ne vous appartient pas' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Composants utilitaires -->
    <ScrollToTop />
    <Notification ref="notificationRef" />
    <ModalAreYouSure v-model="showDeleteModal" title="Supprimer le dataset"
      message="Êtes-vous sûr de vouloir supprimer ce dataset ? Cette action est irréversible." @confirm="confirmDelete"
      @cancel="showDeleteModal = false" />
  </div>
</template>

<script setup lang="ts">
import {
    AlignLeft as AlignLeftIcon,
    BookOpen as BookOpenIcon,
    Check as CheckIcon,
    Clock as ClockIcon,
    Copyright as CopyrightIcon,
    ExternalLink as ExternalLinkIcon,
    Eye as EyeIcon,
    EyeOff as EyeOffIcon,
    File as FileIcon,
    FileText as FileTextIcon,
    FileType as FileTypeIcon,
    Film as FilmIcon,
    Folder as FolderIcon,
    HardDrive as HardDriveIcon,
    Image as ImageIcon,
    Link2 as Link2Icon,
    Link as LinkIcon,
    Music as MusicIcon,
    Pencil as PencilIcon,
    Trash as TrashIcon,
    User as UserIcon,
    X as XIcon
} from 'lucide-vue-next';
import { computed, nextTick, onMounted, ref, type Component } from 'vue';
import { useRoute, useRouter } from 'vue-router';

// Composants
import Breadcrumbs from '../Breadcrumbs.vue';
import Notification from '../Notification.vue';
import MemosSubset from '../subsets/MemosSubset.vue';
import ScrollToTop from '../utils/ScrollToTop.vue';
import ModalAreYouSure from '../utils/modal_areyousure.vue';

// Types
import type { DigitalEntityWithMemos } from '@/api/types/entities/digitalEntity/relations';
import type { Memo } from '@/api/types/memos/types';

// API et stores
import { deleteDigitalEntity, digitalEntities, fetchDigitalEntityWithMemos, updateDigitalEntity } from '@/api/digitalEntities';
import { fetchMemosUuid } from '@/api/memos';
import { useAssetsCacheStore } from '@/stores/assetsCache';
import { useAuthStore } from '@/stores/auth';
import { useBreadcrumbsStore } from '@/stores/breadcrumbs';
import { useUserNamesCacheStore } from '@/stores/userNamesCache';

// Props
interface Props {
  uuid: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps<Props>();

// Composables
const route = useRoute();
const router = useRouter();
const breadcrumbsStore = useBreadcrumbsStore();
const authStore = useAuthStore();
const assetsCacheStore = useAssetsCacheStore();
const userNamesCacheStore = useUserNamesCacheStore();

// État principal
const digitalEntity = ref<DigitalEntityWithMemos | null>(null);
const detailedMemos = ref<Memo[]>([]);
const isLoading = ref(true);
const isEditing = ref(false);
const ownerName = ref<string>('');
const isEditingTitle = ref(false);
const showDeleteModal = ref(false);

// Références
const notificationRef = ref<InstanceType<typeof Notification> | null>(null);
const titleInput = ref<HTMLInputElement | null>(null);

// État d'édition
interface DigitalEntityEditData {
  appellation: string;
  description?: string;
  type?: string;
  format?: string;
  url?: string;
  version?: string;
  copyright?: string;
  de_link?: string;
  file_spec?: string;
  owner?: string;
  is_public: boolean;
  user_id: string;
}

const editedDigitalEntity = ref<DigitalEntityEditData>({
  appellation: '',
  description: '',
  type: '',
  format: '',
  url: '',
  version: '',
  copyright: '',
  de_link: '',
  file_spec: '',
  owner: '',
  is_public: false,
  user_id: authStore.user?.uuid || ''
});
const editedTitle = ref('');

// Gestion des permissions
const isMyAsset = computed((): boolean => {
  return digitalEntity.value?.user_id === authStore.user?.uuid;
});

const canEdit = computed((): boolean => {
  return authStore.isAdmin || isMyAsset.value;
});

const canDelete = computed((): boolean => {
  return authStore.isAdmin || isMyAsset.value;
});

const canViewSubsets = computed((): boolean => {
  // Peut voir les subsets si :
  // 1. L'asset est public, OU
  // 2. L'utilisateur est connecté ET est propriétaire de l'asset
  return digitalEntity.value?.is_public || (authStore.isAuthenticated && isMyAsset.value);
});

// Récupérer l'état depuis history.state
const historyState = window.history.state;
digitalEntity.value = historyState?.digitalEntity || null;

onMounted(async (): Promise<void> => {
  await loadDigitalEntity();
});

const loadDetailedMemos = async (): Promise<void> => {
  if (!digitalEntity.value?.memos) return;

  try {
    const memoPromises = digitalEntity.value.memos.map(memo =>
      fetchMemosUuid(memo.uuid)
    );
    const memosData = await Promise.all(memoPromises);
    detailedMemos.value = memosData.map(data => data[0]);
  } catch (error) {
    console.error('Erreur lors de la récupération des détails des mémos:', error);
  }
};

const loadDigitalEntity = async (): Promise<void> => {
  if (digitalEntity.value) {
    updateBreadcrumb();
    resetEditedData();
    await loadDetailedMemos();
    await loadOwnerName();
    isLoading.value = false;
    return;
  }

  try {
    let entityData;
    // Si l'utilisateur est connecté, utiliser l'API normale
    if (authStore.isAuthenticated) {
      entityData = await fetchDigitalEntityWithMemos(route.params.uuid as string);
    } else {
      // Sinon, utiliser l'API publique qui permet l'accès par UUID
      entityData = await digitalEntities.getPublicByUuidWithMemos(route.params.uuid as string);
    }
    digitalEntity.value = entityData;
    resetEditedData();
    updateBreadcrumb();
    await loadDetailedMemos();
    await loadOwnerName();
  } catch (error) {
    console.error('Erreur lors du chargement du dataset:', error);
    if (authStore.isAuthenticated) {
      await router.push('/mydigitalentities');
    } else {
      await router.push('/');
    }
  } finally {
    isLoading.value = false;
  }
};

const loadOwnerName = async (): Promise<void> => {
  if (!digitalEntity.value?.user_id) {
    ownerName.value = 'Inconnu';
    return;
  }

  try {
    const name = await userNamesCacheStore.getUserNameAsync(digitalEntity.value.user_id);
    ownerName.value = name || 'Utilisateur privé';
  } catch (error) {
    console.error('Erreur lors du chargement du nom du propriétaire:', error);
    ownerName.value = 'Erreur de chargement';
  }
};

const updateBreadcrumb = (): void => {
  const entityName = digitalEntity.value?.appellation || 'Dataset';
  const breadcrumbPath = authStore.isAuthenticated ? '/mydigitalentities' : '/';
  const breadcrumbName = authStore.isAuthenticated ? 'Mes datasets' : 'Accueil';
  breadcrumbsStore.updateBreadcrumbs([
    { name: breadcrumbName, path: breadcrumbPath },
    { name: entityName, path: route.fullPath }
  ]);
};

const resetEditedData = (): void => {
  if (digitalEntity.value) {
    editedDigitalEntity.value = {
      appellation: digitalEntity.value.appellation,
      description: digitalEntity.value.description || '',
      type: digitalEntity.value.type || '',
      format: digitalEntity.value.format || '',
      url: digitalEntity.value.url || '',
      version: digitalEntity.value.version || '',
      copyright: digitalEntity.value.copyright || '',
      de_link: digitalEntity.value.de_link || '',
      file_spec: digitalEntity.value.file_spec || '',
      owner: digitalEntity.value.owner || '',
      is_public: digitalEntity.value.is_public,
      user_id: digitalEntity.value.user_id
    };
    editedTitle.value = digitalEntity.value.appellation;
  }
};

const handleEdit = (): void => {
  if (!canEdit.value) return;
  resetEditedData();
  isEditing.value = true;
};

const handleCancel = (): void => {
  isEditing.value = false;
  isEditingTitle.value = false;
  resetEditedData();
};

const startTitleEdit = (): void => {
  if (!canEdit.value || !digitalEntity.value) return;

  editedTitle.value = digitalEntity.value.appellation || '';
  isEditingTitle.value = true;

  nextTick((): void => {
    titleInput.value?.focus();
  });
};

const cancelTitleEdit = (): void => {
  isEditingTitle.value = false;
  if (digitalEntity.value) {
    editedTitle.value = digitalEntity.value.appellation || '';
  }
};

const handleTitleUpdate = async (): Promise<void> => {
  if (!digitalEntity.value || !canEdit.value || editedTitle.value === digitalEntity.value.appellation) {
    isEditingTitle.value = false;
    return;
  }

  try {
    const updatedEntity = await updateDigitalEntity(digitalEntity.value.uuid, {
      appellation: editedTitle.value.trim(),
      user_id: digitalEntity.value.user_id
    });
    Object.assign(digitalEntity.value, updatedEntity);

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('digitalEntities', digitalEntity.value);

    isEditingTitle.value = false;
    updateBreadcrumb();
    notificationRef.value?.showNotification('Nom mis à jour avec succès', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour du nom:', error);
    notificationRef.value?.showNotification('Erreur lors de la mise à jour', 'error');
  }
};

const handleSave = async (): Promise<void> => {
  if (!digitalEntity.value || !canEdit.value) return;

  // Créer une version propre pour l'API
  const entityToUpdate: Partial<DigitalEntityEditData> = {
    appellation: editedDigitalEntity.value.appellation,
    description: editedDigitalEntity.value.description,
    type: editedDigitalEntity.value.type,
    format: editedDigitalEntity.value.format,
    url: editedDigitalEntity.value.url,
    version: editedDigitalEntity.value.version,
    copyright: editedDigitalEntity.value.copyright,
    owner: editedDigitalEntity.value.owner,
    is_public: editedDigitalEntity.value.is_public,
    user_id: editedDigitalEntity.value.user_id
  };

  // Ajouter de_link et file_spec seulement s'ils ont une valeur
  if (editedDigitalEntity.value.de_link) {
    entityToUpdate.de_link = editedDigitalEntity.value.de_link;
  }
  if (editedDigitalEntity.value.file_spec) {
    entityToUpdate.file_spec = editedDigitalEntity.value.file_spec;
  }

  try {
    const updatedEntity = await updateDigitalEntity(digitalEntity.value.uuid, entityToUpdate);
    digitalEntity.value = { ...updatedEntity, memos: digitalEntity.value.memos };

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('digitalEntities', digitalEntity.value);

    isEditing.value = false;
    isEditingTitle.value = false;
    updateBreadcrumb();
    notificationRef.value?.showNotification('Dataset mis à jour avec succès', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour du dataset:', error);
    notificationRef.value?.showNotification('Erreur lors de la mise à jour', 'error');
  }
};

const handleDelete = (): void => {
  if (!canDelete.value) return;
  showDeleteModal.value = true;
};

const handlePublicToggle = async (): Promise<void> => {
  if (!digitalEntity.value?.uuid || !canEdit.value) return;

  try {
    const updatedEntity = await updateDigitalEntity(digitalEntity.value.uuid, {
      is_public: !digitalEntity.value.is_public,
      user_id: digitalEntity.value.user_id,
      appellation: digitalEntity.value.appellation
    });
    digitalEntity.value = { ...updatedEntity, memos: digitalEntity.value.memos };

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('digitalEntities', digitalEntity.value);

    notificationRef.value?.showNotification('Visibilité mise à jour', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la visibilité:', error);
    notificationRef.value?.showNotification('Erreur lors de la mise à jour de la visibilité', 'error');
  }
};

const confirmDelete = async (): Promise<void> => {
  if (!digitalEntity.value?.uuid || !canDelete.value) return;

  try {
    await deleteDigitalEntity(digitalEntity.value.uuid);

    // Supprimer du cache
    assetsCacheStore.removeAssetFromCache('digitalEntities', digitalEntity.value.uuid);

    notificationRef.value?.showNotification('Dataset supprimé avec succès', 'success');
    router.push('/mydigitalentities');
  } catch (error) {
    console.error('Erreur lors de la suppression du dataset:', error);
    notificationRef.value?.showNotification('Erreur lors de la suppression', 'error');
  }
  showDeleteModal.value = false;
};

// Fonctions utilitaires
const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
};

const handleMemoView = (memo: Memo): void => {
  router.push(`/memos/${memo.uuid}`);
};

// Utilitaires pour l'aperçu
const isImageUrl = (url?: string): boolean => {
  if (!url) return false;
  return /\.(jpeg|jpg|gif|png|webp|svg)$/i.test(url);
};

const isPhotographyType = (type?: string): boolean => {
  if (!type) return false;
  return /photo|image|picture/i.test(type);
};

const getIconForType = (type?: string, format?: string): Component => {
  if (!type && !format) return FileIcon;

  const typeFormat = (type || '').toLowerCase() + (format || '').toLowerCase();

  if (/image|photo|png|jpe?g|gif|webp|svg/i.test(typeFormat)) {
    return ImageIcon;
  } else if (/video|mp4|mov|avi|mkv|film/i.test(typeFormat)) {
    return FilmIcon;
  } else if (/audio|mp3|wav|sound|music/i.test(typeFormat)) {
    return MusicIcon;
  } else if (/document|pdf|doc|txt|text/i.test(typeFormat)) {
    return FileTextIcon;
  }

  return FileIcon;
};
</script>

<style scoped>
/* Les styles personnalisés peuvent être ajoutés ici si nécessaire */
</style>